import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DownloadIcon from "@mui/icons-material/Download";
import { rosterService } from "../../api/service.ts";
import type { RosterEntry, RosterEntryRequest } from "../../api/types";
import { useSeason } from "../../SeasonContext.tsx";
import { useAuth } from "../../AuthContext.tsx";
import { useIsMobile } from "../../hooks/useIsMobile.ts";
import RosterEntryForm from "./RosterEntryForm.tsx";

interface Props {
  open: boolean;
  onClose: () => void;
  /** chiamata dopo ogni modifica, per ricaricare i giocatori della pagina principale */
  onChanged?: () => void;
}

const PARAM_COLUMNS: { key: keyof RosterEntry; label: string }[] = [
  { key: "attack", label: "Att" },
  { key: "defense", label: "Dif" },
  { key: "serve", label: "Bat" },
  { key: "wall", label: "Muro" },
  { key: "setting", label: "Pal" },
  { key: "personality", label: "Pers" },
  { key: "seniority", label: "Anz" },
  { key: "presence", label: "Pres" },
];

export default function RosterDialog({ open, onClose, onChanged }: Props) {
  const isMobile = useIsMobile();
  const { isAdmin } = useAuth();
  const { selectedSeason, seasons } = useSeason();

  // stagione precedente a quella selezionata (le stagioni arrivano ordinate dalla più recente)
  const previousSeason = useMemo(() => {
    if (!selectedSeason) return null;
    const idx = seasons.findIndex((s) => s.id === selectedSeason.id);
    return idx >= 0 ? seasons[idx + 1] ?? null : null;
  }, [seasons, selectedSeason]);

  const [tab, setTab] = useState(0);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [candidates, setCandidates] = useState<RosterEntry[]>([]);
  const [toImport, setToImport] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RosterEntry | null>(null);

  const seasonId = selectedSeason?.id ?? null;

  const load = useCallback(async () => {
    if (seasonId == null) return;
    setLoading(true);
    setError(null);
    try {
      setRoster(await rosterService.getRoster(seasonId));
      if (previousSeason) {
        setCandidates(await rosterService.getImportCandidates(seasonId, previousSeason.id));
      }
    } catch {
      setError("Errore nel caricamento della rosa");
    } finally {
      setLoading(false);
    }
  }, [seasonId, previousSeason]);

  useEffect(() => {
    if (open) {
      load();
      setToImport([]);
    }
  }, [open, load]);

  // rosa vuota e stagione precedente disponibile: si parte dall'import
  useEffect(() => {
    if (open && !loading && roster.length === 0 && candidates.length > 0) setTab(1);
  }, [open, loading, roster.length, candidates.length]);

  const handleSave = async (data: RosterEntryRequest) => {
    if (seasonId == null) return;
    if (editing) await rosterService.update(seasonId, editing.playerId, data);
    else await rosterService.add(seasonId, data);
    setFormOpen(false);
    await load();
    onChanged?.();
  };

  const handleRemove = async (entry: RosterEntry) => {
    if (seasonId == null) return;
    if (!window.confirm(`Togliere ${entry.nickname} dalla rosa ${selectedSeason?.code}?`)) return;
    try {
      await rosterService.remove(seasonId, entry.playerId);
      await load();
      onChanged?.();
    } catch {
      setError("Rimozione non riuscita");
    }
  };

  const handleImport = async () => {
    if (seasonId == null || !previousSeason || toImport.length === 0) return;
    try {
      await rosterService.importFrom(seasonId, previousSeason.id, toImport);
      setToImport([]);
      await load();
      setTab(0);
      onChanged?.();
    } catch {
      setError("Import non riuscito");
    }
  };

  const toggleImport = (id: number) =>
    setToImport((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const activeCount = roster.filter((r) => r.active).length;
  const women = roster.filter((r) => r.active && r.gender === "F").length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>
        Rosa {selectedSeason?.code}
        <Typography component="span" variant="body2" sx={{ ml: 2, opacity: 0.85 }}>
          {activeCount} giocatori ({women} donne)
        </Typography>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab label="Rosa" />
        {previousSeason && isAdmin && (
          <Tab label={`Importa da ${previousSeason.code} (${candidates.length})`} />
        )}
      </Tabs>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
        ) : tab === 0 ? (
          <>
            {roster.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                La rosa {selectedSeason?.code} è vuota: importa chi torna dalla stagione precedente
                oppure aggiungi un nuovo giocatore.
              </Alert>
            )}
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Giocatore</TableCell>
                    <TableCell>Ruoli</TableCell>
                    {!isMobile && PARAM_COLUMNS.map((c) => (
                      <TableCell key={c.key} align="center">{c.label}</TableCell>
                    ))}
                    <TableCell align="center">Skill</TableCell>
                    {isAdmin && <TableCell align="right" />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roster.map((r) => (
                    <TableRow key={r.playerId} hover sx={{ opacity: r.active ? 1 : 0.45 }}>
                      <TableCell>{r.shirtNumber ?? "–"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.nickname} {r.gender === "F" ? "♀" : "♂"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[r.firstName, r.lastName].filter(Boolean).join(" ")}
                          {!r.active && " · uscito dalla rosa"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                          {r.roles.map((role) => <Chip key={role} label={role} size="small" variant="outlined" />)}
                        </Stack>
                      </TableCell>
                      {!isMobile && PARAM_COLUMNS.map((c) => (
                        <TableCell key={c.key} align="center">{r[c.key] as number}</TableCell>
                      ))}
                      <TableCell align="center"><strong>{r.skill}</strong></TableCell>
                      {isAdmin && (
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title={r.active ? "Modifica" : "Modifica e riattiva"}>
                            <IconButton size="small" onClick={() => { setEditing(r); setFormOpen(true); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {r.active && (
                            <Tooltip title="Togli dalla rosa">
                              <IconButton size="small" color="error" onClick={() => handleRemove(r)}>
                                <PersonRemoveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Seleziona chi torna: verrà copiato nella rosa {selectedSeason?.code} con i parametri
              della stagione {previousSeason?.code} (+1 anno di anzianità), modificabili dopo.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button size="small" onClick={() => setToImport(candidates.map((c) => c.playerId))}>Seleziona tutti</Button>
              <Button size="small" onClick={() => setToImport([])}>Deseleziona</Button>
            </Stack>
            {candidates.length === 0 ? (
              <Alert severity="success">Tutti i giocatori della stagione precedente sono già in rosa.</Alert>
            ) : (
              <Table size="small">
                <TableBody>
                  {candidates.map((c) => (
                    <TableRow key={c.playerId} hover onClick={() => toggleImport(c.playerId)} sx={{ cursor: "pointer" }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={toImport.includes(c.playerId)} />
                      </TableCell>
                      <TableCell>
                        <strong>{c.nickname}</strong> {c.gender === "F" ? "♀" : "♂"}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {[c.firstName, c.lastName].filter(Boolean).join(" ")}
                        </Typography>
                      </TableCell>
                      <TableCell>{c.roles.join(", ")}</TableCell>
                      <TableCell align="right">skill {c.skill}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Box>
          {isAdmin && tab === 0 && (
            <Button variant="contained" startIcon={<PersonAddIcon />}
              onClick={() => { setEditing(null); setFormOpen(true); }}>
              Nuovo giocatore
            </Button>
          )}
          {isAdmin && tab === 1 && (
            <Button variant="contained" startIcon={<DownloadIcon />} disabled={toImport.length === 0}
              onClick={handleImport}>
              Importa selezionati ({toImport.length})
            </Button>
          )}
        </Box>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>

      <RosterEntryForm
        open={formOpen}
        entry={editing}
        seasonCode={selectedSeason?.code ?? ""}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />
    </Dialog>
  );
}
